import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { affexyColors as C } from '@/theme/affexyTokens'
import type { PrototypeSession, RespondentStimulus } from '@/services/respondentService'

/** Origins Figma's embed posts its events from (Embed Kit 2.0). */
const FIGMA_ORIGINS = new Set(['https://www.figma.com', 'https://embed.figma.com'])
const SUCCESS_HOLD_MS = 1200
const DEFAULT_LIMIT_MS = 3 * 60 * 1000

type Point = { x: number; y: number }
type MouseData = {
  presentedNodeId?: string
  handled?: boolean
  targetNodeMousePosition?: Point
  nearestScrollingFrameId?: string
  nearestScrollingFrameMousePosition?: Point
  nearestScrollingFrameOffset?: Point
}

/** Where a click landed on the screen picture, in the screen's own pixels.
 *
 *  When the screen itself is what scrolls, the position within it plus how
 *  far it had scrolled is the spot on the full-length screen. A click inside a
 *  nested scrolling area keeps its position within that area — an
 *  approximation the results live with (TODO Z-98 spike: check what Figma
 *  reports for nested scroll frames). */
function clickPosition(d: MouseData): Point | null {
  const pos = d.nearestScrollingFrameMousePosition ?? d.targetNodeMousePosition
  if (!pos) return null
  if (d.nearestScrollingFrameId && d.nearestScrollingFrameId === d.presentedNodeId) {
    return { x: pos.x + (d.nearestScrollingFrameOffset?.x ?? 0), y: pos.y + (d.nearestScrollingFrameOffset?.y ?? 0) }
  }
  return pos
}

function fmtClock(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * One task in a clickable Figma prototype (FaceWise Pro, Z-98).
 *
 * The respondent reads the task, presses "Zaczynam" — only then does the task
 * clock (and the stimulus window on the recording) start — and works in
 * Figma's own embed with the task pinned above it. It ends at the goal screen,
 * on "Nie wiem, pomiń" (or "Skończyłem" when there is no goal) or at the time
 * limit. Every screen change and click the embed reports is logged on the
 * recording clock and handed up once the task ends.
 */
export default function FigmaTaskStimulus({
  stimulus,
  embedUrl,
  recordStartRef,
  onStart,
  onSession,
  onDone,
}: {
  stimulus: RespondentStimulus
  embedUrl: string | undefined
  recordStartRef: React.MutableRefObject<number>
  /** Fired on "Zaczynam": the start of the stimulus window. */
  onStart: () => void
  onSession: (session: PrototypeSession) => void
  onDone: () => void
}) {
  const goal = stimulus.config?.goal_node_id || ''
  const limitMs = stimulus.exposure_ms || DEFAULT_LIMIT_MS

  const [phase, setPhase] = useState<'brief' | 'task' | 'success'>('brief')
  const [loaded, setLoaded] = useState(false)
  const [remaining, setRemaining] = useState(limitMs)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const startedRef = useRef(0)
  const eventsRef = useRef<PrototypeSession['events']>([])
  const finishedRef = useRef(false)

  const now = useCallback(() => {
    const start = recordStartRef.current
    return start ? Date.now() - start : 0
  }, [recordStartRef])

  const finish = useCallback(
    (outcome: PrototypeSession['outcome']) => {
      if (finishedRef.current) return
      finishedRef.current = true
      const rect = iframeRef.current?.getBoundingClientRect()
      const vw = window.innerWidth || 1
      const vh = window.innerHeight || 1
      onSession({
        stimulus_id: stimulus.id,
        started_ms: startedRef.current,
        ended_ms: now(),
        outcome,
        frame: rect ? { x: rect.left / vw, y: rect.top / vh, w: rect.width / vw, h: rect.height / vh } : null,
        events: eventsRef.current,
      })
      if (outcome === 'success') {
        setPhase('success')
        window.setTimeout(onDone, SUCCESS_HOLD_MS)
      } else {
        onDone()
      }
    },
    [now, onDone, onSession, stimulus.id],
  )

  const begin = () => {
    startedRef.current = now()
    onStart()
    setPhase('task')
  }

  // Events from the embed. Anything not from Figma is somebody else's message.
  useEffect(() => {
    if (phase !== 'task') return undefined
    const onMessage = (e: MessageEvent) => {
      if (!FIGMA_ORIGINS.has(e.origin) || !e.data || typeof e.data !== 'object') return
      const { type, data } = e.data as { type?: string; data?: Record<string, unknown> }
      if (!data) return
      if (type === 'INITIAL_LOAD') setLoaded(true)
      if (type === 'PRESENTED_NODE_CHANGED' && typeof data.presentedNodeId === 'string') {
        setLoaded(true)
        eventsRef.current.push({ t_ms: now(), type: 'screen', node_id: data.presentedNodeId })
        if (goal && data.presentedNodeId === goal) finish('success')
      }
      if (type === 'MOUSE_PRESS_OR_RELEASE') {
        const d = data as MouseData
        const pos = clickPosition(d)
        if (!pos || !d.presentedNodeId) return
        eventsRef.current.push({
          t_ms: now(),
          type: 'click',
          node_id: d.presentedNodeId,
          x: pos.x,
          y: pos.y,
          handled: !!d.handled,
        })
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [phase, goal, finish, now])

  // The time limit, shown as a countdown.
  useEffect(() => {
    if (phase !== 'task') return undefined
    const endsAt = Date.now() + limitMs
    const tick = window.setInterval(() => {
      const left = endsAt - Date.now()
      setRemaining(left)
      if (left <= 0) finish('timeout')
    }, 250)
    return () => window.clearInterval(tick)
  }, [phase, limitMs, finish])

  if (phase === 'brief') {
    return (
      <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%', p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700, color: 'rgba(255,255,255,.6)' }}>
          Twoje zadanie
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 26, md: 36 }, maxWidth: 760, lineHeight: 1.25 }}>
          {stimulus.payload}
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,.55)', maxWidth: 520 }}>
          Za chwilę zobaczysz prototyp aplikacji. Klikaj tak, jak w prawdziwej aplikacji. Masz na to{' '}
          {Math.round(limitMs / 60000)} min. Jeśli utkniesz, możesz pominąć zadanie.
        </Typography>
        <Button variant="contained" onClick={begin} disabled={!embedUrl} sx={{ bgcolor: C.coral, px: 4, py: 1.25, fontWeight: 700 }}>
          Zaczynam
        </Button>
        {!embedUrl && (
          <Button onClick={onDone} sx={{ color: 'rgba(255,255,255,.7)' }}>
            Prototyp jest niedostępny, przejdź dalej
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', bgcolor: '#111' }}>
      <Box
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pl: '96px', // clear of the REC badge
          pr: 2,
          py: 1.25,
          bgcolor: '#1B1C38',
          borderBottom: '1px solid rgba(255,255,255,.08)',
        }}
      >
        <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14.5, lineHeight: 1.3 }}>{stimulus.payload}</Typography>
        <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 14, color: remaining < 30000 ? '#FFB1AC' : 'rgba(255,255,255,.6)' }}>
          {fmtClock(remaining)}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={phase !== 'task'}
          onClick={() => finish(goal ? 'gave_up' : 'finished')}
          sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.35)', whiteSpace: 'nowrap' }}
        >
          {goal ? 'Nie wiem, pomiń' : 'Skończyłem'}
        </Button>
      </Box>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <Box
          component="iframe"
          ref={iframeRef}
          src={embedUrl}
          title="Prototyp"
          allow="clipboard-write"
          // Figma's own events say when the prototype is up; the frame's load
          // is the fallback, so a missing event never leaves a spinner on top.
          onLoad={() => window.setTimeout(() => setLoaded(true), 1500)}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, bgcolor: '#111' }}
        />
        {!loaded && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
            <CircularProgress sx={{ color: C.coral }} />
          </Box>
        )}
        {phase === 'success' && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,.55)' }}>
            <Stack alignItems="center" spacing={1.5}>
              <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: C.coral, display: 'grid', placeItems: 'center' }}>
                <CheckIcon sx={{ fontSize: 40, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Gotowe</Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  )
}
